import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Button, Select, Space } from 'antd';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CallCard from './CallCard';
import ViewConfigModal from './ViewConfigModal';
import DownloadDataButton from './DownloadDataButton';
import ExpressionTester from "./ExpressionTester";
import { debounce } from 'lodash';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
// Define the base style to match the metrics pills
const sortPillBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    backgroundColor: '#10b981',
    color: 'white',
    border: '1px solid #10b981',
    borderRadius: '16px',
    cursor: 'pointer',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    opacity: 1,
    transform: 'scale(1)',
    transition: '0.2s',
};

// Draggable sort pill component with integrated handle and remove button.
const DraggableSortPill = React.memo(({ criteria, index, onToggle, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: `sort-${index}` });
    const containerStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 9999 : 1,
        margin: '4px',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
    };

    return (
        <div ref={setNodeRef} style={containerStyle} {...attributes}>
            <div style={{ ...sortPillBaseStyle }}>
                {/* Drag handle placed inside the pill on the left */}
                <span {...listeners} style={{ cursor: 'grab', marginRight: 4 }}>
                    ≡
                </span>
                {/* Pill label and arrow */}
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle();
                    }}
                >
                    {criteria.field === 'rawDate' ? 'Date/Time' : criteria.label}
                    <span style={{ marginLeft: 4 }}>{criteria.direction === 'asc' ? '↑' : '↓'}</span>
                </span>
                {/* Remove button inside the pill */}
                <span
                    style={{ marginLeft: 4, cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                >
                    x
                </span>
            </div>
        </div>
    );
});



const CallList = ({ calls, config, onConfigChange, fieldConfig, onCallClick }) => {
    // Sort criteria state managed within CallList.
    const [sortCriteria, setSortCriteria] = useState(
        config.sortCriteria && config.sortCriteria.length > 0
            ? config.sortCriteria
            : [{ field: 'rawDate', direction: 'desc', label: 'Date/Time' }]
    );
    const [isAddingSort, setIsAddingSort] = useState(false);
    const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);

    const debouncedOnConfigChange = useCallback(
        debounce((newSortCriteria) => {
            onConfigChange({
                ...config,
                sortCriteria: newSortCriteria
            });
        }, 300),
        [config, onConfigChange]
    );

    // Compute sorted calls based on sort criteria.
    const sortedCalls = useMemo(() => {
        if (!calls) return [];
        const validCriteria = sortCriteria.filter((c) => c && c.field);

        return [...calls].sort((a, b) => {
            for (const crit of validCriteria) {
                let aVal = a[crit.field];
                let bVal = b[crit.field];

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    if (aVal < bVal) return crit.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return crit.direction === 'asc' ? 1 : -1;
                } else {
                    aVal = aVal ? String(aVal).toLowerCase() : '';
                    bVal = bVal ? String(bVal).toLowerCase() : '';
                    if (aVal < bVal) return crit.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return crit.direction === 'asc' ? 1 : -1;
                }
            }
            return 0;
        });
    }, [calls, sortCriteria]);

    const handleSortToggle = useCallback((index) => {
        setSortCriteria(prevCriteria => {
            const newCriteria = prevCriteria.map((c, i) =>
                i === index ? { ...c, direction: c.direction === 'asc' ? 'desc' : 'asc' } : c
            );
            debouncedOnConfigChange(newCriteria);
            return newCriteria;
        });
    }, [debouncedOnConfigChange]);

    const handleSortRemove = useCallback((index) => {
        setSortCriteria(prevCriteria => {
            const newCriteria = prevCriteria.filter((_, i) => i !== index);
            debouncedOnConfigChange(newCriteria);
            return newCriteria;
        });
    }, [debouncedOnConfigChange]);

    const handleAddSort = useCallback((value) => {
        const fieldCfg = fieldConfig.find((f) => f.id === value);
        const label = value === 'rawDate'
            ? 'Date/Time'
            : fieldCfg ? fieldCfg.title : value;

        setSortCriteria(prevCriteria => {
            const newCriteria = [...prevCriteria, { field: value, direction: 'asc', label }];
            debouncedOnConfigChange(newCriteria);
            return newCriteria;
        });
        setIsAddingSort(false);
    }, [fieldConfig, debouncedOnConfigChange]);

    // Render the sort header.
    const renderSortHeader = useCallback(() => {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 16px',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#fff',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                        style={{
                            boxSizing: 'border-box',
                            color: 'rgb(107,114,128)',
                            display: 'block',
                            fontFamily:
                                '-apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                            fontSize: '12px',
                            height: '20px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Sort By:
                    </span>
                    <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={({ active, over }) => {
                            if (!over || active.id === over.id) return;
                            setSortCriteria(prevCriteria => {
                                const oldIndex = prevCriteria.findIndex((_, i) => `sort-${i}` === active.id);
                                const newIndex = prevCriteria.findIndex((_, i) => `sort-${i}` === over.id);
                                const newCriteria = arrayMove(prevCriteria, oldIndex, newIndex);
                                debouncedOnConfigChange(newCriteria);
                                return newCriteria;
                            });
                        }}
                    >
                        <SortableContext items={sortCriteria.map((_, i) => `sort-${i}`)} strategy={verticalListSortingStrategy}>
                            <Space size={4} wrap>
                                {sortCriteria.map((criteria, index) => (
                                    <DraggableSortPill
                                        key={`sort-${index}`}
                                        criteria={criteria}
                                        index={index}
                                        onToggle={() => handleSortToggle(index)}
                                        onRemove={() => handleSortRemove(index)}
                                    />
                                ))}
                            </Space>
                        </SortableContext>
                    </DndContext>
                    {isAddingSort ? (
                        <Select
                            autoFocus
                            open
                            showSearch
                            style={{ width: 200 }}
                            placeholder="Select sort field"
                            onChange={handleAddSort}
                            onBlur={() => setIsAddingSort(false)}
                            options={fieldConfig
                                .filter((f) => !sortCriteria.some((c) => c.field === f.id))
                                .map((f) => ({
                                    label: f.id === 'rawDate' ? 'Date/Time' : f.title,
                                    value: f.id,
                                }))}
                        />
                    ) : (
                        <Button type="link" onClick={() => setIsAddingSort(true)}>
                            + Add sort
                        </Button>
                    )}
                </div>
                <div>
                    <Button type="link" onClick={() => setIsConfigModalVisible(true)}>
                        Edit View
                    </Button>
                    <DownloadDataButton
                        data={calls}
                        filteredData={sortedCalls}
                        callListConfig={config}
                        fieldConfig={fieldConfig}
                    />
                </div>
            </div>
        );
    }, [sortCriteria, isAddingSort, fieldConfig, handleSortToggle, handleSortRemove, handleAddSort, calls, sortedCalls, config]);

    // Calculate item size - the height of each CallCard plus margin
    const itemSize = 128; // Adjust as needed (e.g., 112px height + margins)

    // Custom style for the List component to match original appearance
    const listStyle = {
        outline: 'none',
        border: 'none',
        backgroundColor: 'transparent'
    };

    const handleCallClick = useCallback((call) => {
        if (onCallClick) {
            onCallClick(call);
        }
    }, [onCallClick]);

    // Memoized row renderer for the virtualized list
    const rowRenderer = useCallback(({ index, style }) => {
        const call = sortedCalls[index];
        const itemStyle = {
            ...style,
            left: 0,
            right: 0,
            width: '100%'
        };

        return (
            <div style={itemStyle}>
                <CallCard
                    key={call.id || index}
                    call={call}
                    config={config}
                    onClick={handleCallClick}
                    fieldConfig={fieldConfig}
                />
            </div>
        );
    }, [sortedCalls, config, onCallClick, fieldConfig]);

    const renderCount = useRef(0);
    const prevProps = useRef({ calls, config, fieldConfig, onCallClick });

    useEffect(() => {
        // Increment render count
        renderCount.current += 1;

        // Skip first render
        if (renderCount.current === 1) {
            prevProps.current = { calls, config, fieldConfig, onCallClick };
            return;
        }

        // Compare current vs previous props
        const currentProps = { calls, config, fieldConfig, onCallClick };
        const changes = {};

        // Check calls length
        if (prevProps.current.calls?.length !== calls?.length) {
            changes.callsLength = {
                prev: prevProps.current.calls?.length,
                current: calls?.length
            };
        }

        // Check if config changed
        if (prevProps.current.config !== config) {
            changes.config = {
                changed: true,
                // Check specific config properties
                columns: prevProps.current.config?.columns === config?.columns ? 'same ref' : 'changed',
                flags: prevProps.current.config?.flags === config?.flags ? 'same ref' : 'changed',
                tags: prevProps.current.config?.tags === config?.tags ? 'same ref' : 'changed',
                sortCriteria: prevProps.current.config?.sortCriteria === config?.sortCriteria ? 'same ref' : 'changed'
            };
        }

        // Check if callbacks changed
        if (prevProps.current.onConfigChange !== onConfigChange) {
            changes.onConfigChange = 'Reference changed';
        }

        if (prevProps.current.onCallClick !== onCallClick) {
            changes.onCallClick = 'Reference changed';
        }

        // Check fieldConfig
        if (prevProps.current.fieldConfig !== fieldConfig) {
            changes.fieldConfig = 'Reference changed';
        }

        // Log results if changes were found
        if (Object.keys(changes).length > 0) {
           // console.log(`CallList re-render #${renderCount.current}, changes detected:`, changes);
        } else {
           // console.log(`CallList re-render #${renderCount.current}, but no prop changes detected!`);
        }

        // Update previous props reference
        prevProps.current = { calls, config, fieldConfig, onCallClick };
    }, [calls, config, fieldConfig, onCallClick]);
    //console.log('Render CallList');
    return (
        <div>
            {renderSortHeader()}
            {/* Virtualized list container for call cards */}
            <div style={{
                height: '600px',
                border: '1px solid #f0f0f0',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                overflow: 'hidden'
            }}>
                {sortedCalls.length === 0 ? (
                    <div style={{
                        padding: '24px',
                        textAlign: 'center',
                        color: '#6b7280',
                        backgroundColor: '#f9fafb',
                        height: '100%'
                    }}>
                        No calls match the current filters
                    </div>
                ) : (
                    <AutoSizer>
                        {({ height, width }) => (
                            <List
                                height={height}
                                width={width}
                                itemCount={sortedCalls.length}
                                itemSize={itemSize}
                                overscanCount={5}
                                style={listStyle}
                                className="calls-virtual-list"
                            >
                                {rowRenderer}
                            </List>
                        )}
                    </AutoSizer>
                )}
            </div>
            <div style={{
                padding: '8px 16px',
                borderTop: 'none',
                color: '#6b7280',
                fontSize: '14px',
                textAlign: 'right'
            }}>
                {sortedCalls.length} calls
            </div>
            <ViewConfigModal
                visible={isConfigModalVisible}
                onClose={() => setIsConfigModalVisible(false)}
                config={config}
                onConfigChange={onConfigChange}
                fieldConfig={fieldConfig}
            />
        </div>
    );
};


export default React.memo(CallList, (prevProps, nextProps) => {
    // Only re-render if the call IDs change, not if the entire objects change reference
    const callsEqual = () => {
        // Handle null/undefined cases
        if (!prevProps.calls && !nextProps.calls) return true;
        if (!prevProps.calls || !nextProps.calls) return false;

        // Compare lengths first (quick check)
        if (prevProps.calls.length !== nextProps.calls.length) return false;

        // Compare IDs only - much faster than comparing entire objects
        const prevIds = new Set(prevProps.calls.map(call => call.id));
        return nextProps.calls.every(call => prevIds.has(call.id));
    };

    // Check if configs are equal using JSON.stringify for deep comparison
    const configIsEqual = () => {
        return JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config);
    };

    // Check if fieldConfig is equal using simplified comparison
    const fieldConfigEqual = () => {
        // Handle null/undefined cases
        if (!prevProps.fieldConfig && !nextProps.fieldConfig) return true;
        if (!prevProps.fieldConfig || !nextProps.fieldConfig) return false;

        // Compare lengths first
        if (prevProps.fieldConfig.length !== nextProps.fieldConfig.length) return false;

        // Compare field IDs
        const prevIds = prevProps.fieldConfig.map(field => field.id).join(',');
        const nextIds = nextProps.fieldConfig.map(field => field.id).join(',');

        return prevIds === nextIds;
    };

    // Ignore onConfigChange as it should be a stable reference

    // Only re-render if any of the important props changed
    const areEqual = callsEqual() && configIsEqual() && fieldConfigEqual();

    // For debugging
    if (!areEqual) {
      /*  console.log('CallList re-render reasons:', {
            callsChanged: !callsEqual(),
            configChanged: !configIsEqual(),
            fieldConfigChanged: !fieldConfigEqual()
        });*/
    }

    return areEqual; // true = don't re-render, false = do re-render
});
